import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getProjectById } from '../services/projectService';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    getProjectById(id).then((data) => {
      setProject(data);
      setLoading(false);
      setActiveImage(0);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream-50 gap-4">
        <div className="w-32 h-0.5 bg-cream-200 overflow-hidden rounded">
          <div className="h-full w-1/2 bg-gold animate-shimmer" />
        </div>
        <p className="text-charcoal/30 font-sans text-xs uppercase tracking-widest-plus">Loading</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-50">
        <div className="text-center">
          <h2 className="text-3xl font-serif font-semibold text-charcoal mb-4">Project Not Found</h2>
          <Link to="/projects" className="btn-premium">
            <span>Back to Projects</span>
          </Link>
        </div>
      </div>
    );
  }

  const gallery = Array.isArray(project?.gallery) && project.gallery.length > 0
    ? project.gallery
    : project?.image
      ? [project.image]
      : [];

  return (
    <div className="bg-cream-50">
      {/* Hero Gallery */}
      <section className="relative h-[60vh] sm:h-[70vh]">
        <AnimatePresence mode="wait">
          <motion.img
            key={activeImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            src={gallery[activeImage]}
            alt={project.name}
            className="w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/20" />

        {/* Project Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
          <div className="max-w-7xl mx-auto">
            <span className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-sans font-medium uppercase tracking-widest-plus bg-charcoal/70 backdrop-blur-sm text-white border-l-2 border-gold w-fit mb-4">
              <span className={`w-1.5 h-1.5 rounded-full ${
                project.status === 'Completed'
                  ? 'bg-emerald-400'
                  : project.status === 'Ongoing'
                  ? 'bg-gold'
                  : 'bg-white/60'
              }`} />
              {project.status}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-2">
              {project.name}
            </h1>
            <div className="flex items-center text-cream-200/80 font-sans text-sm">
              <svg className="w-4 h-4 mr-2 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {project.location}
            </div>
          </div>
        </div>
      </section>

      {/* Thumbnail Gallery */}
      <div className="bg-charcoal py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`shrink-0 w-24 h-16 overflow-hidden transition-all duration-300 ${
                  i === activeImage
                    ? 'ring-2 ring-gold opacity-100'
                    : 'opacity-40 hover:opacity-70'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Project Details */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <h2 className="text-2xl font-serif font-semibold text-charcoal mb-6">About This Project</h2>
                <p className="text-charcoal/60 font-sans leading-relaxed mb-10">
                  {project.longDescription}
                </p>

                {/* Key Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12">
                  {[
                    { label: 'Type', value: project.type },
                    { label: 'Units', value: project.units },
                    { label: 'Total Units', value: project.totalUnits },
                    { label: 'Area Range', value: project.area },
                  ].map((detail, i) => (
                    <div key={i} className="bg-white p-5 border-t-2 border-gold/30">
                      <p className="text-[10px] font-sans text-charcoal/40 uppercase tracking-widest-plus mb-2">{detail.label}</p>
                      <p className="text-charcoal font-serif font-semibold">{detail.value}</p>
                    </div>
                  ))}
                </div>

                {/* Amenities */}
                <h3 className="text-xl font-serif font-semibold text-charcoal mb-6">Amenities & Features</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
                  {project.amenities.map((amenity, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center space-x-3 p-4 bg-white card-premium"
                    >
                      <svg className="w-4 h-4 text-gold shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-charcoal/70 font-sans text-sm">{amenity}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Location Map */}
                <h3 className="text-xl font-serif font-semibold text-charcoal mb-6">Location</h3>
                <div className="relative">
                  {/* Decorative gold frame */}
                  <div className="absolute -top-3 -left-3 w-full h-full border border-gold/15 hidden lg:block" />
                  <div className="relative w-full h-80 bg-cream-100">
                    <iframe
                      src={project.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Project Location"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="sticky top-24 space-y-6"
              >
                {/* Builder Info Card */}
                <div className="bg-cream-100 p-8">
                  <h3 className="text-lg font-serif font-semibold text-charcoal mb-4">Developer</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-14 h-14 bg-gold flex items-center justify-center text-charcoal text-xl font-serif font-bold">
                      A
                    </div>
                    <div>
                      <p className="font-serif font-semibold text-charcoal">Avdhoot Group</p>
                      <p className="text-xs font-sans text-charcoal/40 uppercase tracking-widest-plus">Burhanpur, MP</p>
                    </div>
                  </div>
                  <p className="text-charcoal/50 font-sans text-sm leading-relaxed mb-4">
                    3+ years of trust, 5+ projects delivered. Burhanpur's most reliable real estate developer.
                  </p>
                  <Link
                    to="/about"
                    className="link-underline text-gold font-sans text-sm font-medium inline-flex items-center"
                  >
                    Learn More
                    <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>

                {/* Contact Card */}
                <div className="bg-charcoal p-8 text-white">
                  {/* Gold accent line */}
                  <div className="w-8 h-0.5 bg-gold mb-6" />
                  <h3 className="text-lg font-serif font-semibold mb-4">Interested in This Project?</h3>
                  <p className="text-cream-200/50 font-sans text-sm mb-6">
                    Schedule a site visit or request more information about this project.
                  </p>
                  <div className="space-y-3">
                    <Link
                      to="/contact"
                      className="btn-premium-filled w-full text-center"
                    >
                      <span>Schedule a Visit</span>
                    </Link>
                    <a
                      href="tel:+919479303333"
                      className="btn-premium border-gold/30 text-gold w-full text-center"
                    >
                      <span>Call Us Now</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
